import type { TFunction } from 'i18next';
import type {
  EntityPickerCopy,
  EntityPickerEntityType,
  EntityPickerSuggestNew
} from '@bliks-io/ui';
import type { PickerSlotMode } from './hooks/usePickerSlotSuggestions';

/**
 * Build the i18n `copy` block for an entity picker. All wrappers share the
 * same key shape (`entityPicker.<entity>.<x>`) — extracting the mapping here
 * keeps each wrapper to one call. `team` and `role` are the kind-fixed
 * presentation copies of the OrgFunction picker (ADR 0004). In `'create'` mode
 * the suggest-new labels are swapped for the `create*` keys of the same entity.
 */
export function buildEntityPickerCopy(
  t: TFunction,
  entity: EntityPickerEntityType | 'team' | 'role',
  slotMode: PickerSlotMode = 'suggest'
): EntityPickerCopy {
  // Each empty-state CTA is optional on its own (the shared copy contract lets
  // a picker label only the with-query state) — an absent key must stay
  // absent, never a raw key name.
  const emptyStateCtaBlank =
    t(`entityPicker.${entity}.emptyStateCtaBlank`, { defaultValue: '' }) || undefined;
  const hasEmptyStateCtaWithQuery = !!t(`entityPicker.${entity}.emptyStateCtaWithQuery`, {
    defaultValue: ''
  });
  const createCopy: Partial<EntityPickerCopy> =
    slotMode === 'create'
      ? {
          suggestRowMeta: t(`entityPicker.${entity}.createRowMeta`),
          emptyStateCtaWithQuery: (query) =>
            t(`entityPicker.${entity}.createCtaWithQuery`, { query }),
          emptyStateCtaBlank: t(`entityPicker.${entity}.createCtaBlank`)
        }
      : {};
  return {
    title: t(`entityPicker.${entity}.title`),
    description: t(`entityPicker.${entity}.description`),
    searchPlaceholder: t(`entityPicker.${entity}.searchPlaceholder`),
    emptyMessage: t(`entityPicker.${entity}.empty`),
    primaryCtaSingular: (count) => t(`entityPicker.${entity}.saveSingular`, { count }),
    primaryCtaPlural: (count) => t(`entityPicker.${entity}.savePlural`, { count }),
    saveLabel: t('common.save'),
    cancelLabel: t('common.cancel'),
    backLabel: t('common.back'),
    recommendedBadge: t('entityPicker.common.recommendedBadge'),
    matchInFieldTemplate: (field) => t('entityPicker.common.matchInFieldTemplate', { field }),
    departmentFilterAriaLabel: t('entityPicker.common.departmentFilterAriaLabel'),
    selectedSectionLabel: (count) => t('entityPicker.common.selectedSectionLabel', { count }),
    removeChipAriaLabel: (name) => t('entityPicker.common.removeChipAriaLabel', { name }),
    switchToAllLabel: (count) => t('entityPicker.common.switchToAllLabel', { count }),
    switchToBucketsLabel: t('entityPicker.common.switchToBucketsLabel'),
    noDataTitle: t(`entityPicker.${entity}.noDataTitle`, { defaultValue: '' }) || undefined,
    noDataMessage: t(`entityPicker.${entity}.noDataMessage`, { defaultValue: '' }) || undefined,
    allGroupSelectedLabel: (count) => t('entityPicker.common.allGroupSelectedLabel', { count }),
    allGroupRestLabel: (count) => t(`entityPicker.${entity}.allGroupRestLabel`, { count }),
    showAllCtaTitle: t(`entityPicker.${entity}.showAllCtaTitle`),
    showAllCtaMeta: (count) => t(`entityPicker.${entity}.showAllCtaMeta`, { count }),
    suggestRowTitle: t(`entityPicker.${entity}.suggestRowTitle`, { defaultValue: '' }) || undefined,
    suggestRowMeta: t(`entityPicker.${entity}.suggestRowMeta`, { defaultValue: '' }) || undefined,
    emptyStateCtaWithQuery: hasEmptyStateCtaWithQuery
      ? (query) => t(`entityPicker.${entity}.emptyStateCtaWithQuery`, { query })
      : undefined,
    emptyStateCtaBlank,
    ...createCopy
  };
}

/**
 * Format a per-row stats line: "in 3 von 5 Aufnahmen (60 %)".
 */
export function formatStatsLine(
  t: TFunction,
  stats: { occurrences: number; samples: number }
): string {
  const percent = stats.samples > 0 ? Math.round((stats.occurrences / stats.samples) * 100) : 0;
  return t('entityPicker.common.statsLine', {
    occurrences: stats.occurrences,
    samples: stats.samples,
    percent
  });
}

/**
 * Build the suggest-new trigger for entities that support it. The modal
 * itself owns the labels (via `EntityPickerCopy.suggestRowTitle` and
 * `EntityPickerCopy.emptyStateCta*`); wrappers only supply the action.
 */
export function buildSuggestNew(onTrigger: (query: string) => void): EntityPickerSuggestNew {
  return { onTrigger };
}
